import type { ChangeEvent, ReactNode } from "react";
import type { Control, FieldValues, Path, RefCallBack } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Textarea } from "@shared-kernel/primary/shared/shadcn/ui/textarea";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { LONG_TEXT_ROWS } from "./long-text-rows";

/**
 * Le champ grandit avec son contenu — `rows` n'est plus qu'un plancher — sans jamais avaler la
 * fenêtre. Les navigateurs sans `field-sizing` retombent sur `rows`, qui reste correct.
 */
const GROW_WITH_CONTENT = "[field-sizing:content] max-h-[50vh]";

/**
 * Ce que `FormField` / `Controller` rend pour un champ de texte. `value` est `unknown` parce que
 * react-hook-form le type depuis le formulaire, et qu'un chemin générique (`Path<T>`) ne dit plus
 * de quelle clé il s'agit — c'est ce qui obligeait les appelants à caster.
 */
interface ControlledTextField {
  name: string;
  value: unknown;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur: () => void;
  ref: RefCallBack;
}

interface LongTextareaProps {
  field: ControlledTextField;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  /** Classes de la saisie elle-même, pour ce que `rows` ne dit pas (une hauteur de plancher, un fond). */
  className?: string;
  "data-testid"?: string;
}

/**
 * La saisie d'un champ de texte long. À utiliser directement quand le `FormItem` porte autre chose
 * qu'une étiquette (une case à cocher, un bouton de suppression, un compteur de caractères) ; sinon
 * préférer `LongTextField`, qui monte aussi l'étiquette et le message d'erreur.
 *
 * La valeur est ramenée à une chaîne ici, une fois pour toutes : un `reset()` sur une réponse d'API
 * peut poser `null` là où le formulaire est typé `string`, et React ferait alors passer le champ en
 * non contrôlé.
 */
export const LongTextarea = ({ field, rows = LONG_TEXT_ROWS, className, ...props }: LongTextareaProps) => (
  <Textarea
    {...field}
    {...props}
    value={typeof field.value === "string" ? field.value : ""}
    rows={rows}
    className={cn(GROW_WITH_CONTENT, className)}
  />
);

interface LongTextFieldProps<T extends FieldValues> extends Omit<LongTextareaProps, "field"> {
  control: Control<T>;
  name: Path<T>;
  label?: ReactNode;
  description?: ReactNode;
  /** Classes du `FormItem`, pour la mise en page du champ dans sa grille. */
  itemClassName?: string;
}

/**
 * Un champ de texte long complet : étiquette, description, saisie et message de validation. Seul
 * format de texte long depuis l'issue #369 — aucun formatage, juste des sauts de ligne.
 */
export const LongTextField = <T extends FieldValues>({
  control,
  name,
  label,
  description,
  itemClassName,
  ...textareaProps
}: LongTextFieldProps<T>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={itemClassName}>
        {label && <FormLabel>{label}</FormLabel>}
        {description && <FormDescription>{description}</FormDescription>}
        <FormControl>
          <LongTextarea field={field} {...textareaProps} />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);
